/**
 * THE declaration of the sub-pages under Tactical E2E: one row per routed `/tactical`
 * page that is a PAGE rather than a record, each carrying where it appears in the nav.
 *
 * WHY THIS FILE EXISTS, AND WHY IT IS SHAPED LIKE `settingsNav.ts`. Tactical E2E had no
 * sub-page precedent: the primary nav in `Layout.tsx` is flat, and Settings is the only
 * entry that nests — one level, derived from a declaration, with the derivation
 * test-enforced. The lesson that produced `settingsNav.ts` is the one that applies here:
 * `/settings/quality` and `/settings/audit` were routed, their pages worked, and neither
 * appeared in any menu, so two finished features were reachable only by typing a URL.
 * That is the tenth instance of a landed-but-unwired feature in this codebase, and the
 * Internal E2E launch page would have been the eleventh.
 *
 * So the Tactical children are not written either. They are DERIVED from the rows below,
 * `Layout.tsx` holds no `/tactical` sub-path of its own, and
 * `tests/test_tactical_nav_and_route_order.py` compares this table against `App.tsx`'s
 * own route literals in BOTH directions.
 *
 * WHAT IS NOT A SURFACE HERE. `/tactical/:id` and `/tactical/:id/items/:itemId` are
 * RECORDS, not pages: their entries are batches and faxes, enumerated at runtime from the
 * batch list, and a nav row for them would have nothing to point at. They are declared
 * `hidden` with that reason rather than omitted, so the two-directional guard stays
 * meaningful — an omission and a decision look identical in a table that allows gaps.
 *
 * THE ROUTE ORDER IS A SEPARATE PROPERTY, and it is load-bearing: `/tactical/internal`
 * must be declared BEFORE `/tactical/:id` in `App.tsx`, or the parameterised route
 * captures the literal string `internal` and renders a blank batch report instead of
 * erroring. The guard asserts the ordering, not merely the presence.
 */

/** The parent nav entry these hang under, as `Layout.tsx`'s own `navItems` spells it. */
export const TACTICAL_PATH = '/tactical'

/**
 * Where a Tactical surface appears. The same discriminated union as `settingsNav.ts`'s
 * `MenuPlacement`, for the same reason: a row that decides nothing does not compile, so
 * there is no default placement and no optional field to forget.
 */
export type TacticalPlacement =
  | { readonly kind: 'top'; readonly label: string }
  | { readonly kind: 'child'; readonly label: string; readonly under: string }
  | { readonly kind: 'hidden'; readonly reason: string }

export interface TacticalSurface {
  /** The routed path, exactly as `App.tsx` spells it. */
  readonly path: string
  readonly placement: TacticalPlacement
}

/** Declared in nav order. */
export const TACTICAL_SURFACES: readonly TacticalSurface[] = [
  { path: TACTICAL_PATH, placement: { kind: 'top', label: 'Tactical E2E' } },
  {
    // B3. The internal relay launch page. A CHILD rather than a fifth top-level entry:
    // it launches the same kind of batch against the same kind of job, and the only
    // difference is the path the fax takes — so it belongs beside its parent, not beside
    // Reports.
    path: '/tactical/internal',
    placement: { kind: 'child', label: 'Internal E2E', under: TACTICAL_PATH },
  },
  {
    path: '/tactical/:id',
    placement: {
      kind: 'hidden',
      reason:
        'A RECORD, not a page. Its entries are individual batches, listed on Tactical ' +
        'E2E itself, so a nav row for it would have nothing to point at.',
    },
  },
  {
    path: '/tactical/:id/items/:itemId',
    placement: {
      kind: 'hidden',
      reason:
        'A RECORD, not a page. Its entries are individual faxes within one batch, ' +
        'reached from that batch report, so a nav row for it would point at nothing.',
    },
  },
]

/** One rendered child row. */
export interface TacticalMenuItem {
  path: string
  label: string
}

/**
 * The children of one parent nav path, in declaration order.
 *
 * Returns an empty array for a parent with none, which is what lets `Layout.tsx` call it
 * for EVERY nav item unconditionally — a call site that had to know which parents have
 * children is a second place for the two lists to drift apart.
 */
export function tacticalChildren(parentPath: string): TacticalMenuItem[] {
  return TACTICAL_SURFACES.filter(
    (surface): surface is TacticalSurface & { placement: { kind: 'child'; label: string; under: string } } =>
      surface.placement.kind === 'child' && surface.placement.under === parentPath,
  ).map(surface => ({ path: surface.path, label: surface.placement.label }))
}
